import type { FirestoreSlide } from '../firestore/Slide/schema'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import {
  RubricType,
  SlideDeckType,
  SlideType,
  SlideDeckState,
  SlideDeckMaterialType,
  MediaType,
} from '../types'
import type { FirestoreSlideDeck } from '../firestore/SlideDeck/schema'
import type { FirestoreSlideDeckMaterial } from '../firestore/SlideDeckMaterial/schema'
import type { FirestoreSlideDeckAuthor } from '../firestore/SlideDeckAuthor/schema'
import type { SlideDeckExhibit } from '../models/SlideDeckExhibit'
import type { Media } from '../models/Media'
import type en from '../../../../apps/web/src/i18n/locale/en.json'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { SlideQuestion } from '../models/SlideQuestion'
import { type SlideModel } from '../models/SlideModel'
import type { SlideRubric } from '../models/SlideRubric'
import { checkIsFreeType } from '../sources-of-truth/checkIsFreeType'
import { getYouTubeVideoId } from './youtube'
import { validateSlideQuestion } from '../sources-of-truth/validators'
import { PRE_MEETING_QUESTION_SLIDE_ID } from '../constants'
import { type SlideDeckRole } from '../models/SlideDeckRole'
import { RoomState } from '../models/RoomState'

export const MIN_PRICE = 10

/** Slide types that play a full-screen video, so an exhibit can't be shown accessibly. */
export const SLIDE_TYPES_WITHOUT_EXHIBITS: SlideType[] = [
  SlideType.video,
  SlideType.dynamicIntroVideo,
  SlideType.endOfSession,
]

export interface ValidationContext {
  /** The slide deck being validated */
  slideDeck: FirestoreSlideDeck
  /** All slides in the deck */
  slides: SlideModel[]
  /** All materials (attachments) associated with the deck */
  materials: FirestoreSlideDeckMaterial[]
  /** Authors of the slide deck */
  authors: FirestoreSlideDeckAuthor[]
  /** Exhibits (images/figures) used in the deck */
  exhibits: SlideDeckExhibit[]
  /** Unique identifier of the slide deck */
  slideDeckId: string
  /** The id of the first slide if one is present. It is used when we need to recalculate the slide count (by updating the updatedAt of one of the slides) */
  firstSlideId?: string
  /** All the mediaObjects */
  mediaObjects: {
    [k: string]: Media
  }
  /** All rubrics associated with the deck */
  rubrics: SlideRubric[]

  /** All question (interactive elements) associated with the deck */
  questions: SlideQuestion[]

  /** All roles associated with the deck */
  roles: SlideDeckRole[]
}

export type ErrorKey = keyof (typeof en)['admin_details']['errors']

export const WarningKeys = {
  /** if any discussion slide has a solo bot, show a warning when any are missing a solo bot */
  DISCUSSION_SLIDE_MISSING_SOLO_BOT: 'discussion_slide_missing_solo_bot',
  /** if a non-demo slide deck doesn't have an End of Session slide */
  MISSING_END_OF_SESSION_SLIDE: 'missing_end_of_session_slide',
  /** if a rubric-optional experience type has no rubrics attached */
  RUBRIC_MISSING: 'rubric_missing',
}

const RUBRIC_OPTIONAL_TYPES: SlideDeckType[] = [
  SlideDeckType.dialog,
  SlideDeckType.oral_exam,
  SlideDeckType.tutorial,
]

type WarningKeysType = typeof WarningKeys
type WarningKey = WarningKeysType[keyof WarningKeysType]

export interface ValidationError {
  key: ErrorKey
  options?: Record<string, string>
}

export interface AdminSlideDeckPublishValidationWarning {
  key: WarningKey
  options?: Record<string, string>
  description: string
}

/**
 * Validates a slide deck and returns an array of validation errors
 * @param context - All required data for validation
 * @param repository - Firebase repository instance for async operations
 * @returns Array of validation errors, empty if validation passes
 */
export const validateSlideDeck = async (
  context: ValidationContext,
  repository: FirebaseRepository
): Promise<{
  errors: ValidationError[]
  warnings: AdminSlideDeckPublishValidationWarning[]
}> => {
  const isDemo = context.slideDeck.slideDeckType === SlideDeckType.demo

  const errors: ValidationError[] = [
    ...validateGeneralDetails(context),
    ...validateSlides(context),
    ...(isDemo ? [] : validateAuthors(context)), // not for demos
    ...validateExhibits(context),
    ...validateTypeSpecificRules(context),
    ...validateMaterials(context),
    // demos may place any slide type after Session Processing
    ...(isDemo ? [] : validateProcessingDataSlide(context)),
    ...validatePartialProcessingDataSlide(context),
    ...validateEndOfSessionSlide(context),
    ...validateRubrics(context),
    ...validateSoloBot(context),
    ...validateRoles(context),
    ...validatePrice(context),
  ]

  const questionErrors = await validateQuestions(repository, context)

  errors.push(...questionErrors)

  // Only run the slide-count check if no other errors
  if (errors.length === 0) {
    const slideCountError = validateSlideCount({
      slides: context.slides,
      slideDeck: context.slideDeck,
    })

    if (slideCountError) {
      errors.push(slideCountError)
    }
  }

  // add warnings
  const warnings: AdminSlideDeckPublishValidationWarning[] = [
    ...validateSoloBotDiscussionSlides(context),
    ...validateEndOfSessionSlideWarning(context),
    ...validateRubricsWarning(context),
  ]

  return { errors, warnings }
}

// Helper functions for different validation aspects
const validateGeneralDetails = ({
  slideDeck,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []
  const isDemo = slideDeck.slideDeckType === SlideDeckType.demo
  if (!slideDeck.slideDeckName) {
    errors.push({ key: 'experience_must_have_title' })
  }
  if (!slideDeck.slideDeckTeaser) {
    errors.push({ key: 'experience_must_have_subtitle' })
  }
  if (!slideDeck.slideDeckDescription) {
    errors.push({ key: 'experience_must_have_description' })
  }
  if (!slideDeck.slideDeckImageURL) {
    errors.push({ key: 'experience_must_have_slide_deck_logo' })
  }
  if (!slideDeck.slideDeckVersion && slideDeck.slideDeckVersion !== '0.0.0') {
    errors.push({ key: 'experience_must_have_version' })
  }
  if (!slideDeck.slideDeckLearningObjectives.length && !isDemo) {
    errors.push({ key: 'experience_must_have_learning_objective' })
  }
  if (!slideDeck.slideDeckDisciplines.length && !isDemo) {
    errors.push({ key: 'experience_must_have_discipline' })
  }
  if (
    [SlideDeckState.deleted, SlideDeckState.hidden].includes(
      slideDeck.slideDeckState
    )
  ) {
    errors.push({ key: 'experience_slide_deck_deleted_or_hidden' })
  }

  return errors
}

const validateSlides = ({
  mediaObjects,
  slides,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  if (slides.length === 0) {
    errors.push({ key: 'experience_must_have_one_slide' })
    return errors
  }

  if (slides[0].data.slideType === SlideType.soloQuiz) {
    errors.push({
      key: 'experience_solo_quiz_as_first_slide',
      options: {
        slideId: slides[0].id,
        slideName: slides[0].data.slideName,
        slideNumber: String(slides[0].data.slideOrder + 1),
      },
    })
  }

  slides.forEach((slide) => {
    errors.push(...validateSlideByType(mediaObjects, slide.id, slide.data))
  })

  return errors
}

const validateSlideByType = (
  mediaObjects: {
    [k: string]: Media
  },
  slideId: string,
  slide: FirestoreSlide
): ValidationError[] => {
  const errors: ValidationError[] = []
  const options = {
    slideId,
    slideName: slide.slideName,
    slideNumber: String(slide.slideOrder + 1),
  }

  if (!slide.slideName) {
    errors.push({ key: 'slide_missing_slide_name', options })
  }

  switch (slide.slideType) {
    case SlideType.exhibitGrid:
      // Potential future validation: ensure that at least one exhibit is present.
      break
    case SlideType.groupQuiz:
    case SlideType.interactivePoll:
    case SlideType.soloQuiz:
    case SlideType.interactiveQuiz:
      // Potential future validation: validate that there is at least one interactive element.
      break
    case SlideType.sessionResults:
      // Potential future validation: must come after processing data.
      break
    case SlideType.processingData:
      // Potential future validation: must come before session results.
      break
    case SlideType.professorFeedback:
    case SlideType.discussion:
      if (slide.slideImageURL && !slide.slideImageAltText) {
        errors.push({
          key: 'slide_missing_image_alt_text',
          options,
        })
      }
      break
    case SlideType.image:
      if (!slide.slideImageURL) {
        errors.push({ key: 'slide_missing_image_url', options })
      }
      if (!slide.slideImageAltText) {
        errors.push({
          key: 'slide_missing_image_alt_text',
          options,
        })
      }
      break
    case SlideType.video:
      if (slide.slideVideoYouTubeURL) {
        if (!getYouTubeVideoId(slide.slideVideoYouTubeURL)) {
          errors.push({ key: 'slide_youtube_url_invalid', options })
        }
        if (
          typeof slide.slideVideoDuration !== 'number' ||
          slide.slideVideoDuration <= 0
        ) {
          errors.push({ key: 'slide_youtube_duration_required', options })
        }
        break
      }
      if (!slide.slideVideoURL) {
        errors.push({ key: 'slide_missing_video_url', options })
      }
      if (slide.mediaId) {
        const media = mediaObjects[slide.mediaId]
        // A missing or hidden asset is reported by the general global-media
        // check at the end of this function (it applies to every slide type).
        // Here we only add video-specific problems when the asset is actually
        // present and visible.
        if (media && !media.data.hidden) {
          if (media.data.mediaType !== MediaType.Video) {
            errors.push({ key: 'global_media_wrong_type', options })
          } else if (media.data.mediaVideoError) {
            errors.push({ key: 'global_media_video_error', options })
          } else if (typeof media.data.mediaVideoDuration !== 'number') {
            errors.push({
              key: 'global_media_missing_video_duration',
              options,
            })
          }
        }
      } else if (typeof slide.slideVideoDuration !== 'number') {
        errors.push({ key: 'slide_missing_video_duration', options })
      }
      if (slide.slideVideoError) {
        errors.push({ key: 'slide_video_error', options })
      }
      break
    case SlideType.markdown:
    case SlideType.waitingRoom:
      if (!slide.slideMarkdown) {
        errors.push({ key: 'slide_missing_markdown', options })
      }
      break
  }

  // Any slide type can reference a global asset via `mediaId` (video, image,
  // discussion, professor feedback, processing data). "Deleting" a global
  // asset only marks it hidden, so a missing OR hidden asset must fail
  // validation regardless of slide type.
  if (slide.mediaId) {
    const media = mediaObjects[slide.mediaId]
    if (!media || media.data.hidden) {
      errors.push({ key: 'global_media_missing', options })
    }
  }

  return errors
}

const validateAuthors = ({
  slideDeck,
  authors,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  if (
    slideDeck.slideDeckType !== SlideDeckType.dialog &&
    authors.length === 0
  ) {
    errors.push({ key: 'experience_must_have_author' })
  }

  authors.forEach((author) => {
    if (!author.authorImageURL) {
      errors.push({ key: 'experience_author_without_image' })
    }
  })

  return errors
}

const validateExhibits = ({
  exhibits,
  slides,
  questions,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  const slideById = new Map<string, SlideModel>()
  for (const slide of slides) {
    slideById.set(slide.id, slide)
  }

  // Build a map of questionId -> slideId for quick lookups
  const questionSlideMap = new Map<string, string>()
  for (const question of questions) {
    if (question.data.slideId) {
      questionSlideMap.set(question.id, question.data.slideId)
    }
  }

  exhibits.forEach((exhibit) => {
    const baseOptions = {
      exhibitId: exhibit.id,
      exhibitName: exhibit.data.exhibitName,
    }
    const hasMarkdown =
      typeof exhibit.data.exhibitMarkdown === 'string' &&
      exhibit.data.exhibitMarkdown.trim().length > 0

    if (!hasMarkdown) {
      if (!exhibit.data.exhibitAltText) {
        errors.push({
          key: 'exhibit_missing_image_alt_text',
          options: baseOptions,
        })
      }
      if (!exhibit.data.exhibitURL) {
        errors.push({
          key: 'exhibit_missing_image_url',
          options: baseOptions,
        })
      }
    }

    if (!exhibit.data.slideId) {
      errors.push({
        key: 'exhibit_missing_slide_id',
        options: baseOptions,
      })
    }

    const exhibitSlide = exhibit.data.slideId
      ? slideById.get(exhibit.data.slideId)
      : undefined

    // Exhibits would render over the full-screen video, which is inaccessible.
    // No slideId in the options: the publish error must link to the exhibit,
    // not the slide.
    if (
      exhibitSlide &&
      SLIDE_TYPES_WITHOUT_EXHIBITS.includes(exhibitSlide.data.slideType)
    ) {
      errors.push({
        key: 'exhibit_on_unsupported_slide',
        options: {
          ...baseOptions,
          slideNumber: String(exhibitSlide.data.slideOrder + 1),
          slideName: exhibitSlide.data.slideName,
        },
      })
    }

    // Validate that all input sources reference questions on slides BEFORE the exhibit's slide
    const inputSources = exhibit.data.inputSources ?? []
    if (inputSources.length > 0 && exhibitSlide) {
      const exhibitSlideOrder = exhibitSlide.data.slideOrder

      for (const source of inputSources) {
        const questionSlideId = questionSlideMap.get(source.questionId)
        if (!questionSlideId) continue
        const questionSlideOrder =
          slideById.get(questionSlideId)?.data.slideOrder
        if (
          questionSlideOrder !== undefined &&
          questionSlideOrder >= exhibitSlideOrder
        ) {
          errors.push({
            key: 'exhibit_input_source_after_exhibit_slide',
            options: {
              ...baseOptions,
              variableName: source.variableName,
              questionSlideNumber: String(questionSlideOrder + 1),
              exhibitSlideNumber: String(exhibitSlideOrder + 1),
            },
          })
        }
      }
    }
  })

  return errors
}

const validateTypeSpecificRules = ({
  slideDeck,
  materials,
  questions,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  // A demo is entered straight from a demo room, which has no section or
  // assignment behind it — so there is nowhere to send a student to take a
  // pre-meeting quiz. A demo deck carrying one hard-blocks entry, so refuse
  // to publish it. See MeetingCubit.currentUserMustCompletePreWorkQuiz.
  if (slideDeck.slideDeckType === SlideDeckType.demo) {
    for (const questionModel of questions) {
      if (!questionModel.isPreMeetingQuestion) continue
      errors.push({
        key: 'experience_demo_cannot_have_pre_meeting_quiz',
        options: {
          questionId: questionModel.id,
          questionName: questionModel.data.question,
        },
      })
    }
  }

  if (slideDeck.slideDeckType === SlideDeckType.original) {
    const hasTrailer = materials.some(
      (material) =>
        material.materialType === SlideDeckMaterialType.mp4 &&
        material.materialLink &&
        material.materialName === 'Trailer'
    )
    if (!hasTrailer) {
      errors.push({ key: 'experience_must_have_trailer' })
    }

    const hasTrailerThumbnail = materials.some(
      (material) =>
        material.materialType === SlideDeckMaterialType.img &&
        material.materialLink &&
        material.materialName === 'Trailer Thumbnail'
    )
    if (!hasTrailerThumbnail) {
      errors.push({ key: 'experience_must_have_trailer_thumbnail' })
    }
  }

  return errors
}

const validateMaterials = ({
  materials,
  mediaObjects,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  materials.forEach((material) => {
    const options = { materialName: material.materialName }

    // PDF materials can reference a global asset via `globalMediaId`. As with
    // slides, "deleting" that asset only marks it hidden, so a missing or
    // hidden asset must block publish.
    if (material.globalMediaId) {
      const media = mediaObjects[material.globalMediaId]
      if (!media || media.data.hidden) {
        errors.push({ key: 'material_media_missing', options })
      }
    }

    switch (material.materialType) {
      case SlideDeckMaterialType.url:
      case SlideDeckMaterialType.pdf:
      case SlideDeckMaterialType.mp3:
      case SlideDeckMaterialType.webm:
      case SlideDeckMaterialType.featuredLarge:
      case SlideDeckMaterialType.img:
      case SlideDeckMaterialType.mp4:
        if (!material.materialLink) {
          errors.push({
            key: 'material_missing_link',
            options,
          })
        }
        break
    }
  })

  return errors
}

/**
 * Compares the number of loaded slides against the deck's persisted
 * `slideDeckSlideCount`. A mismatch means the deck graph is inconsistent (edits
 * not fully flushed / the aggregate is stale). This is a pure synchronous
 * comparison — no reactive waiting — so it runs identically on client and
 * server.
 *
 * `slideDeckSlideCount` is kept correct by the Go Firestore slide trigger
 * (golang/server/triggers/slide_trigger.go -> SlideDeck.UpdateSlideCount), which
 * recomputes it from a live count on every slide create/update/delete. This
 * check is therefore read-only diagnostics; it no longer touches a slide to
 * force a recompute (validation must not mutate). If the stored count ever
 * drifts, any slide edit re-fires the trigger and heals it.
 */
export const validateSlideCount = (
  context: Pick<ValidationContext, 'slides' | 'slideDeck'>
): ValidationError | null => {
  const serverSlideCount = context.slideDeck.slideDeckSlideCount
  const { slides } = context

  if (slides.length === 0) return null

  const localSlideCount = slides.length
  if (localSlideCount === serverSlideCount) return null

  return { key: 'experience_slide_count_mismatch' }
}

export const validateProcessingDataSlide = (
  context: ValidationContext
): ValidationError[] => {
  const errors: ValidationError[] = []
  const slides = context.slides

  const processingDataSlideIndex = slides.findIndex(
    (slide) => slide.data.slideType === SlideType.processingData
  )

  if (processingDataSlideIndex === -1) return errors

  // Nothing may come after the Session Processing slide except Results or
  // End of Session slides. Flag every slide after it that is neither.
  const allowedAfter = [SlideType.sessionResults, SlideType.endOfSession]
  for (let i = processingDataSlideIndex + 1; i < slides.length; i++) {
    const slide = slides[i]
    if (allowedAfter.includes(slide.data.slideType)) continue
    errors.push({
      key: 'experience_slide_after_processing_data',
      options: {
        slideId: slide.id,
        slideName: slide.data.slideName,
        slideNumber: String(slide.data.slideOrder + 1),
      },
    })
  }

  return errors
}

export const validatePartialProcessingDataSlide = (
  context: ValidationContext
): ValidationError[] => {
  const errors: ValidationError[] = []
  const { slideDeck, slides } = context

  const partialSlides = slides.filter(
    (slide) => slide.data.slideType === SlideType.partialProcessingData
  )

  if (partialSlides.length === 0) return errors

  // partial session processing is a demo-only feature
  if (slideDeck.slideDeckType !== SlideDeckType.demo) {
    errors.push({ key: 'experience_partial_processing_demo_only' })
  }

  // only one partial processing slide is allowed per deck
  if (partialSlides.length > 1) {
    errors.push({ key: 'experience_multiple_partial_processing_slides' })
  }

  // the partial slide must be followed by an official Session Processing (or
  // End of Session) slide so the remaining rubrics are always scored by the
  // final grading run
  const partialIndex = slides.findIndex(
    (slide) => slide.data.slideType === SlideType.partialProcessingData
  )
  const hasOfficialProcessingAfter = slides
    .slice(partialIndex + 1)
    .some(
      (slide) =>
        slide.data.slideType === SlideType.processingData ||
        slide.data.slideType === SlideType.endOfSession
    )

  if (!hasOfficialProcessingAfter) {
    errors.push({
      key: 'experience_partial_processing_requires_processing_after',
    })
  }

  return errors
}

export const validateEndOfSessionSlide = (
  context: ValidationContext
): ValidationError[] => {
  const errors: ValidationError[] = []
  const slides = context.slides

  const endOfSessionSlideIndex = slides.findIndex(
    (slide) => slide.data.slideType === SlideType.endOfSession
  )

  if (endOfSessionSlideIndex > -1) {
    // End of Session should be the last slide, if not - error
    if (endOfSessionSlideIndex !== slides.length - 1) {
      const endOfSessionSlide = slides[endOfSessionSlideIndex]
      errors.push({
        key: 'experience_slide_end_of_session_not_last_slide',
        options: {
          slideName: endOfSessionSlide.data.slideName,
        },
      })
    }

    // End of Session cannot be used with Processing Data or Session Results slides
    const hasProcessingDataSlide = slides.some(
      (slide) => slide.data.slideType === SlideType.processingData
    )
    const hasSessionResultsSlide = slides.some(
      (slide) => slide.data.slideType === SlideType.sessionResults
    )

    if (hasProcessingDataSlide) {
      errors.push({
        key: 'experience_slide_end_of_session_with_processing_data',
      })
    }

    if (hasSessionResultsSlide) {
      errors.push({
        key: 'experience_slide_end_of_session_with_session_results',
      })
    }
  }

  return errors
}

const validateRubrics = ({
  rubrics,
  slides,
  slideDeck,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []
  const isDemo = slideDeck.slideDeckType === SlideDeckType.demo

  // we don't need rubrics for demo slide decks, and rubric-optional types
  // surface this as a warning instead (see validateRubricsWarning)
  if (
    !isDemo &&
    rubrics.length === 0 &&
    !RUBRIC_OPTIONAL_TYPES.includes(slideDeck.slideDeckType)
  ) {
    errors.push({ key: 'experience_must_have_rubric' })
  }

  // we do need valid slide config for each rubric
  for (const rubric of rubrics) {
    // Demo Delivery rubrics evaluate a demo presenter and make no sense on
    // other experience types
    if (!isDemo && rubric.rubricType === RubricType.demoDelivery) {
      errors.push({
        key: 'rubric_demo_delivery_demo_only',
        options: {
          rubricId: rubric.id,
          rubricName: rubric.data.rubric,
        },
      })
    }

    const result = rubric.hasValidSlideConfiguration(slides)

    if (result.valid) continue // slide config is valid

    switch (result.reason) {
      case 'endBeforeStart':
        errors.push({
          key: 'rubric_start_slide_after_end_slide',
          options: {
            rubricId: rubric.id,
            rubricName: rubric.data.rubric,
          },
        })
        break
      case 'slideNoLongerExists':
        errors.push({
          key: 'rubric_must_have_existing_slide',
          options: {
            rubricId: rubric.id,
            rubricName: rubric.data.rubric,
          },
        })
        break
      case 'missingEnd':
        errors.push({
          key: 'rubric_has_start_without_end_slide',
          options: {
            rubricId: rubric.id,
            rubricName: rubric.data.rubric,
          },
        })
        break
    }
  }

  return errors
}

const validateRubricsWarning = ({
  rubrics,
  slideDeck,
}: ValidationContext): AdminSlideDeckPublishValidationWarning[] => {
  const warnings: AdminSlideDeckPublishValidationWarning[] = []
  const isDemo = slideDeck.slideDeckType === SlideDeckType.demo

  if (
    !isDemo &&
    rubrics.length === 0 &&
    RUBRIC_OPTIONAL_TYPES.includes(slideDeck.slideDeckType)
  ) {
    warnings.push({
      key: WarningKeys.RUBRIC_MISSING,
      description: 'This experience has no rubrics attached.',
    })
  }

  return warnings
}

const validateSoloBotDiscussionSlides = ({
  questions,
  slides,
}: ValidationContext): AdminSlideDeckPublishValidationWarning[] => {
  const { slidesWithSoloBot, slidesWithoutSoloBot } = slides.reduce(
    (acc, slide) => {
      if (slide.data.slideType !== SlideType.discussion) return acc
      const hasSoloBot = questions.some(
        (question) =>
          question.data.slideId === slide.id &&
          SlideQuestion.soloBotQuestionTypes.includes(
            question.data.questionType
          )
      )

      // only care about discussion slides
      hasSoloBot
        ? acc.slidesWithSoloBot.push(slide)
        : acc.slidesWithoutSoloBot.push(slide)

      return acc
    },
    {
      slidesWithSoloBot: [] as ValidationContext['slides'],
      slidesWithoutSoloBot: [] as ValidationContext['slides'],
    }
  )

  const warnings: AdminSlideDeckPublishValidationWarning[] = []

  if (slidesWithSoloBot.length > 0 && slidesWithoutSoloBot.length > 0) {
    // push a warning for each slide without solo bot
    slidesWithoutSoloBot.forEach((slide) => {
      warnings.push({
        key: WarningKeys.DISCUSSION_SLIDE_MISSING_SOLO_BOT,
        options: {
          slideName: slide.data.slideName,
          slideNumber: (slides.indexOf(slide) + 1).toString(),
        },
        description: `This discussion slide is missing a Solo Bot. Other discussion slides have a Solo Bot enabled.`,
      })
    })
  }

  return warnings
}

const validateEndOfSessionSlideWarning = ({
  slideDeck,
  slides,
}: ValidationContext): AdminSlideDeckPublishValidationWarning[] => {
  const warnings: AdminSlideDeckPublishValidationWarning[] = []

  if (slideDeck.slideDeckType === SlideDeckType.demo) {
    return warnings
  }

  const hasEndOfSessionSlide = slides.some(
    (slide) => slide.data.slideType === SlideType.endOfSession
  )

  if (!hasEndOfSessionSlide) {
    warnings.push({
      key: WarningKeys.MISSING_END_OF_SESSION_SLIDE,
      description:
        'This non-demo slide deck does not have an End of Session slide.',
    })
  }

  return warnings
}

const validateQuestions = async (
  repository: FirebaseRepository,
  { questions, slides }: ValidationContext
): Promise<ValidationError[]> => {
  const errors: ValidationError[] = []

  const endOfSessionSlides = slides.filter(
    (slide) => slide.data.slideType === SlideType.endOfSession
  )

  const slidesWithTalkTracksSoFar = new Set<string>()
  const slideIds = new Set(slides.map((slide) => slide.id))
  slideIds.add(PRE_MEETING_QUESTION_SLIDE_ID) // pre-meeting quiz is a special case

  const slideContextById = new Map<
    string,
    { slideName: string; slideNumber: string }
  >()
  for (const slide of slides) {
    slideContextById.set(slide.id, {
      slideName: slide.data.slideName,
      slideNumber: String(slide.data.slideOrder + 1),
    })
  }

  questions.forEach(async (questionModel) => {
    const { data } = questionModel
    const { question, slideId, groupSlideId, questionType } = data
    const questionSlideIds = [slideId, groupSlideId].filter(
      (id): id is string => Boolean(id)
    )

    const slideContext = slideId ? slideContextById.get(slideId) : undefined
    const slideName = slideContext?.slideName ?? ''
    const slideNumber = slideContext?.slideNumber

    const baseQuestionOptions: Record<string, string> = {
      // if the problem is that the question is missing, use the slide name
      // for better UX
      questionName: question || slideName,
      slideName,
      questionId: questionModel.id,
      questionType: String(questionType),
    }
    if (slideId) baseQuestionOptions.slideId = slideId
    if (slideNumber) baseQuestionOptions.slideNumber = slideNumber

    const singleValidationErrors = await validateSlideQuestion(
      { repository },
      data
    )

    for (const error of singleValidationErrors) {
      errors.push({
        key: error.key,
        options: { ...baseQuestionOptions },
      })
    }

    if (questionSlideIds.length === 0) {
      // at least one must be present
      return errors.push({
        key: 'question_invalid_slide_id',
        options: {
          questionId: questionModel.id,
          questionName: question,
        },
      })
    }

    const allMatching = questionSlideIds.every((id) => slideIds.has(id))

    const questionSlides = slides.filter((slide) =>
      questionSlideIds.includes(slide.id)
    )

    // BL-4567 exemption: Solo Quiz / Group Quiz no longer list suggestedTimer
    // in compatibleQuestionTypes (see SlideModel), but decks saved before that
    // change may still carry a leftover suggestedTimer question on such a slide.
    // That leftover is inert — the editor toggle is hidden for these types so it
    // can't be re-added, and Slide.hasSuggestedTimer ignores it at runtime. The
    // next save of that slide deletes it (SlideDeckSlideForm forces
    // useSuggestedTimer off). Blocking publish over a question the admin cannot
    // see or remove would strand those legacy decks, so don't treat a leftover
    // suggestedTimer question on those two quiz types as a slide mismatch.
    const isLegacyTimerOnQuiz =
      questionType === SlideQuestionType.suggestedTimer &&
      questionSlides.every(
        (slide) =>
          slide.data.slideType === SlideType.soloQuiz ||
          slide.data.slideType === SlideType.interactiveQuiz
      )
    const allSlidesCompatible =
      isLegacyTimerOnQuiz ||
      questionSlides.every((slide) =>
        slide.compatibleQuestionTypes.includes(questionType)
      )

    if (!allSlidesCompatible) {
      return errors.push({
        key: 'question_slide_mismatch',
        options: { ...baseQuestionOptions },
      })
    }

    if (!allMatching) {
      // error, question is not attached to a valid slide
      return errors.push({
        key: 'question_invalid_slide_id',
        options: {
          questionId: questionModel.id,
          questionName: question,
        },
      })
    }

    // only validate attached talk tracks
    if (!slideId || questionType !== SlideQuestionType.talkTrack) return

    // error if more than one talk track on slide
    if (slidesWithTalkTracksSoFar.has(slideId))
      return errors.push({
        key: 'question_talk_track_duplicate',
        options: { ...baseQuestionOptions },
      })

    slidesWithTalkTracksSoFar.add(slideId)
  })
  // end Promise.all

  // check if end of session slide has talk track but no quiz
  endOfSessionSlides.forEach(({ id, data: { slideName, slideOrder } }) => {
    if (!slidesWithTalkTracksSoFar.has(id)) return

    const slideHasQuiz = questions.some(
      (q) => q.data.slideId === id && q.isQuiz
    )

    if (!slideHasQuiz)
      errors.push({
        key: 'question_talk_track_end_of_session_no_quiz',
        options: {
          slideId: id,
          slideName,
          slideNumber: String(slideOrder + 1),
        },
      })
  })

  return errors
}

const validateSoloBot = ({
  questions,
  slides,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []

  for (const slide of slides) {
    const slideQuestions = questions.filter(
      (question) => question.data.slideId === slide.id
    )

    const soloBot = slideQuestions.find(({ questionType }) =>
      SlideQuestion.soloBotQuestionTypes.includes(
        questionType as SlideQuestionType
      )
    )
    const moderationTasks = slideQuestions.filter(
      (question) =>
        question.data.questionType === SlideQuestionType.moderationTask
    )

    if (soloBot && moderationTasks.length === 0) {
      errors.push({
        key: 'solo_bot_missing_moderation_task',
        options: {
          slideId: slide.id,
          slideName: slide.data.slideName,
          slideNumber: String(slide.data.slideOrder + 1),
        },
      })
    }
  }

  return errors
}

const validateRoles = ({
  slideDeck,
  roles,
  rubrics,
  materials,
  exhibits,
}: ValidationContext): ValidationError[] => {
  const errors: ValidationError[] = []
  const isDemo = slideDeck.slideDeckType === SlideDeckType.demo

  // every role must be used, except on demos, where a role may exist purely to
  // seat attendees. The presenter role is always exempt from that exemption —
  // it exists to be evaluated, so nothing assigned to it is a mistake.
  for (const role of roles) {
    if (isDemo && !role.isPresenter) continue

    const roleName = role.data.roleName

    const roleIsAssignedToItem = [...rubrics, ...materials, ...exhibits].some(
      (item) => {
        const data = 'data' in item ? item.data : item
        return data.assignmentRoles?.includes(role.id)
      }
    )

    if (!roleIsAssignedToItem) {
      errors.push({
        key: 'role_not_assigned_to_item',
        options: {
          roleId: role.id,
          roleName,
        },
      })
    }
  }

  // the presenter holds the only presenter seat, so attendees need a role of
  // their own to be assigned to
  if (
    isDemo &&
    roles.some((role) => role.isPresenter) &&
    !roles.some((role) => !role.isPresenter)
  ) {
    errors.push({ key: 'presenter_role_requires_attendee_role' })
  }

  const maxGroupSize = RoomState.maxAllowedUsers

  // get the min sum of all roles
  const totalMin = roles.reduce((sum, r) => sum + r.data.roleMin, 0)
  if (totalMin > maxGroupSize) {
    errors.push({
      key: 'roles_minimum_sum_invalid',
      options: {
        maxGroupSize: maxGroupSize.toString(),
      },
    })
  }

  // check all exhibits, materials, rubrics have valid role assignments
  rubrics.forEach((rubric) => {
    const assignedRoleId = rubric.data.assignmentRoles?.[0]
    const role = assignedRoleId
      ? roles.find((role) => role.id === assignedRoleId)
      : undefined
    if (assignedRoleId && !role) {
      errors.push({
        key: 'rubric_role_invalid',
        options: {
          rubricId: rubric.id,
          rubricName: rubric.data.rubric,
        },
      })
    } else if (
      isDemo &&
      rubric.rubricType === RubricType.demoDelivery &&
      !role?.isPresenter
    ) {
      // Demo Delivery rubrics evaluate the presenter; no role or any other
      // role is a misconfiguration.
      errors.push({
        key: 'rubric_demo_delivery_requires_presenter_role',
        options: {
          rubricId: rubric.id,
          rubricName: rubric.data.rubric,
        },
      })
    }
  })

  exhibits.forEach((exhibit) => {
    if (!exhibit.data.assignmentRoles || !exhibit.data.assignmentRoles.length)
      return
    const roleExists = roles.some(
      (role) => role.id === exhibit.data.assignmentRoles?.[0]
    )
    if (!roleExists) {
      errors.push({
        key: 'exhibit_role_invalid',
        options: {
          exhibitId: exhibit.id,
          exhibitName: exhibit.data.exhibitName,
        },
      })
    }
  })
  materials.forEach((material) => {
    if (!material.assignmentRoles || !material.assignmentRoles.length) return
    const roleExists = roles.some(
      (role) => role.id === material.assignmentRoles?.[0]
    )
    if (!roleExists) {
      errors.push({
        key: 'material_role_invalid',
        options: {
          materialName: material.materialName,
        },
      })
    }
  })
  return errors
}

export const validatePrice = (
  context: ValidationContext
): ValidationError[] => {
  const errors: ValidationError[] = []

  const isFreeType = checkIsFreeType(context.slideDeck.slideDeckType)
  const price = context.slideDeck.slideDeckPrice

  if (!isFreeType && price < MIN_PRICE) {
    errors.push({
      key: 'experience_price_too_low',
      options: {
        price: MIN_PRICE.toString(),
      },
    })
  }

  if (isFreeType && price > 0) {
    errors.push({
      key: 'experience_price_too_high',
    })
  }

  return errors
}
